import { ArrowRightLeft, Edit, Mail, MapPin, MoreVertical, Search, Shield, ShieldOff, Upload, User, UserCheck, UserPlus, UserX, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Button } from '../components/ui/Button';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/Tabs';
import BulkImportDialog from '../components/ui/BulkImportDialog';
import BulkTransferLocationsDialog from '../components/ui/BulkTransferLocationsDialog';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import CreateUserDialog from '../components/ui/CreateUserDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/DropdownMenu';
import { Input } from '../components/ui/Input';
import { Pagination } from '../components/ui/Pagination';
import RejectUserDialog from '../components/ui/RejectUserDialog';
import { Skeleton } from '../components/ui/Skeleton';
import { Table, TableColumn } from '../components/ui/Table';
import UpdateUserDialog from '../components/ui/UpdateUserDialog';
import TransferUserLocationDialog from '../components/ui/TransferUserLocationDialog';
import SingleLocationSelector from '../components/SingleLocationSelector';
import useLocale from '../contexts/LocaleContext';
import { getCurrentUser } from '../hooks/useAuth';
import { useRoles } from '../hooks/useRoles';
import {
  useApproveUser,
  useBulkImportUsers,
  useBulkToggleCentralLevel,
  useBulkTransferLocations,
  useCreateUser,
  useDeactivateUser,
  useDownloadBulkTransferLocationsTemplate,
  useDownloadOnboardingTemplate,
  useRejectUser,
  useUpdateUser,
  useUsers,
} from '../hooks/useUsers';
import { hasAnyPermission, hasPermission } from '../lib/permissions';
import type { BulkImportResultDto, CreateUserDto, UpdateUserDto, UserDto } from '../types/api';

export default function Users() {
  const currentUser = getCurrentUser();
  const { dictionary } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  // Debounce search term to avoid too many API calls
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to first page when debounced search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  // Initialize statusFilter from URL query parameter
  const [statusFilter, setStatusFilter] = useState<'ACTIVE' | 'INACTIVE' | undefined>(() => {
    const status = searchParams.get('status');
    if (status === 'ACTIVE') return 'ACTIVE';
    if (status === 'INACTIVE') return 'INACTIVE';
    // Default to 'ALL' (undefined) if no status or status is 'ALL'
    return undefined;
  });

  // Sync statusFilter with URL when URL changes (browser back/forward)
  useEffect(() => {
    const status = searchParams.get('status');
    let urlStatus: 'ACTIVE' | 'INACTIVE' | undefined;
    if (status === 'ACTIVE') urlStatus = 'ACTIVE';
    else if (status === 'INACTIVE') urlStatus = 'INACTIVE';
    // Default to 'ALL' (undefined) if no status or status is 'ALL'
    else urlStatus = undefined;

    setStatusFilter(prev => {
      if (prev !== urlStatus) {
        return urlStatus;
      }
      return prev;
    });
  }, [searchParams]);

  // Update URL query parameter when statusFilter changes
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    const currentStatus = params.get('status');

    let newStatus: string;
    if (statusFilter === 'ACTIVE') {
      newStatus = 'ACTIVE';
    } else if (statusFilter === 'INACTIVE') {
      newStatus = 'INACTIVE';
    } else {
      newStatus = 'ALL';
    }

    // Update URL if different or if no status is set (initial load)
    if (currentStatus !== newStatus || (!currentStatus && newStatus === 'ALL')) {
      params.set('status', newStatus);
      setSearchParams(params, { replace: true });
    }

    // Reset to first page when status filter changes
    setCurrentPage(1);
  }, [statusFilter, searchParams, setSearchParams]);

  // Role + location filters
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [locationFilter, setLocationFilter] = useState<string | undefined>(undefined);
  const { data: rolesResponse } = useRoles({ limit: 100 });
  const roleOptions = rolesResponse?.data ?? [];

  // Reset to first page when any filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [roleFilter, locationFilter]);

  const { data: usersResponse, isLoading } = useUsers({
    status: statusFilter,
    search: debouncedSearchTerm.trim() || undefined,
    roleId: roleFilter || undefined,
    geoLocationId: locationFilter,
    page: currentPage,
    limit: itemsPerPage,
  });

  const users = usersResponse?.data || [];
  const totalItems = usersResponse?.total || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const approveUserMutation = useApproveUser();
  const rejectUserMutation = useRejectUser();
  const deactivateUserMutation = useDeactivateUser();
  const updateUserMutation = useUpdateUser();
  const createUserMutation = useCreateUser();
  const downloadTemplateMutation = useDownloadOnboardingTemplate();
  const bulkImportMutation = useBulkImportUsers();
  const bulkCentralLevelMutation = useBulkToggleCentralLevel();
  const downloadBulkTransferTemplateMutation = useDownloadBulkTransferLocationsTemplate();
  const bulkTransferPreviewMutation = useBulkTransferLocations();
  const bulkTransferCommitMutation = useBulkTransferLocations();
  const canCreateUser = hasPermission(currentUser as any, 'USERS_CREATE');
  const canUpdateStatus = hasPermission(currentUser as any, 'USERS_UPDATE_STATUS');
  const canApproveUser = hasPermission(currentUser as any, 'USERS_APPROVE');
  const canAssignLocations = hasPermission(currentUser as any, 'USERS_ASSIGN_LOCATIONS');

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [bulkImportDialogOpen, setBulkImportDialogOpen] = useState(false);
  const [bulkTransferDialogOpen, setBulkTransferDialogOpen] = useState(false);

  // Row selection for bulk central-level toggle
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());

  const toggleUserSelection = (userId: string) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedUserIds.size === users.length) {
      setSelectedUserIds(new Set());
    } else {
      setSelectedUserIds(new Set(users.map(u => u.id)));
    }
  };

  const handleBulkImport = async (file: File): Promise<BulkImportResultDto> => {
    return bulkImportMutation.mutateAsync(file);
  };

  const handleBulkTransferPreview = async (file: File) => {
    return bulkTransferPreviewMutation.mutateAsync({ file, dryRun: true });
  };

  const handleBulkTransferCommit = async (file: File) => {
    return bulkTransferCommitMutation.mutateAsync({ file, dryRun: false });
  };

  const handleBulkCentralLevel = async (isCentralLevel: boolean) => {
    const selectedUsers = users.filter(u => selectedUserIds.has(u.id));
    const emails = selectedUsers.map(u => u.email);
    const result = await bulkCentralLevelMutation.mutateAsync({ emails, isCentralLevel });
    if (result.notFound.length > 0) {
      toast(`Not found: ${result.notFound.join(', ')}`, { icon: '⚠️' });
    }
    setSelectedUserIds(new Set());
  };

  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    userId: string;
    userName: string;
    action: 'approve' | 'deactivate' | null;
  }>({
    isOpen: false,
    userId: '',
    userName: '',
    action: null,
  });

  const [rejectDialog, setRejectDialog] = useState<{
    isOpen: boolean;
    userId: string;
    userName: string;
  }>({
    isOpen: false,
    userId: '',
    userName: '',
  });

  const [updateDialog, setUpdateDialog] = useState<{
    isOpen: boolean;
    user: UserDto | null;
  }>({
    isOpen: false,
    user: null,
  });

  const [transferLocationDialog, setTransferLocationDialog] = useState<{
    isOpen: boolean;
    user: UserDto | null;
  }>({
    isOpen: false,
    user: null,
  });

  // Check if user can manage users
  if (!hasPermission(currentUser as any, 'USERS_READ')) {
    return (
      <div className='p-6'>
        <div className='bg-red-50 border-l-4 border-red-500 p-6 rounded-lg'>
          <p className='text-red-600'>{dictionary['users.access_denied']}</p>
        </div>
      </div>
    );
  }

  const handleApproveUser = async () => {
    await approveUserMutation.mutateAsync(confirmDialog.userId);
    setConfirmDialog({ isOpen: false, userId: '', userName: '', action: null });
  };

  const handleRejectUser = async (reason: string) => {
    await rejectUserMutation.mutateAsync({ id: rejectDialog.userId, reason });
    setRejectDialog({ isOpen: false, userId: '', userName: '' });
  };

  const handleDeactivateUser = async () => {
    await deactivateUserMutation.mutateAsync(confirmDialog.userId);
    setConfirmDialog({ isOpen: false, userId: '', userName: '', action: null });
  };

  const handleUpdateUser = async (data: UpdateUserDto) => {
    if (!updateDialog.user) return;
    await updateUserMutation.mutateAsync({ id: updateDialog.user.id, data });
    setUpdateDialog({ isOpen: false, user: null });
  };

  const handleCreateUser = async (data: CreateUserDto) => {
    await createUserMutation.mutateAsync(data);
    setCreateDialogOpen(false);
  };

  const formatRoleLabel = (roleCode?: string) => {
    if (!roleCode) return 'N/A';
    return roleCode.replace(/_+$/g, '').replace(/_/g, ' ');
  };

  const columns: TableColumn<UserDto>[] = [
    ...(canUpdateStatus
      ? [
          {
            key: 'select',
            label: (
              <input
                type='checkbox'
                className='rounded border-gray-300'
                checked={users.length > 0 && selectedUserIds.size === users.length}
                onChange={toggleSelectAll}
                aria-label='Select all users'
              />
            ),
            render: (user: UserDto) => (
              <input
                type='checkbox'
                className='rounded border-gray-300'
                checked={selectedUserIds.has(user.id)}
                onChange={() => toggleUserSelection(user.id)}
                onClick={e => e.stopPropagation()}
                aria-label={`Select ${user.name}`}
              />
            ),
          } as TableColumn<UserDto>,
        ]
      : []),
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: user => (
        <div className='flex items-center gap-2.5'>
          <div className='bg-secondary rounded-full p-1.5 flex-shrink-0'>
            <User className='h-3.5 w-3.5 text-primary' />
          </div>
          <span className='font-medium text-gray-900 whitespace-nowrap'>{user.name}</span>
        </div>
      ),
    },
    {
      key: 'email',
      label: 'Email',
      sortable: true,
      headerClassName: 'hidden sm:table-cell',
      cellClassName: 'hidden sm:table-cell',
      render: user => (
        <div className='flex items-center gap-1.5 text-sm text-gray-600'>
          <Mail className='h-3.5 w-3.5 text-gray-400 flex-shrink-0' />
          <span className='whitespace-nowrap'>{user.email}</span>
        </div>
      ),
    },
    {
      key: 'role',
      label: 'Role',
      sortable: true,
      headerClassName: 'hidden md:table-cell',
      cellClassName: 'hidden md:table-cell',
      render: user => {
        const roleCode = user.assignedRole?.code ?? user.role;
        const displayRole = user.assignedRole?.name ?? formatRoleLabel(roleCode);
        return (
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${
            roleCode === 'ADMIN'
              ? 'bg-purple-100 text-purple-700'
              : roleCode === 'PROVINCE_ADMIN' ||
                  roleCode === 'DISTRICT_ADMIN' ||
                  roleCode === 'SECTOR_ADMIN' ||
                  roleCode === 'CELL_ADMIN' ||
                  roleCode === 'VILLAGE_ADMIN'
                ? 'bg-secondary text-primary'
                : 'bg-gray-100 text-gray-700'
          }`}
        >
          {displayRole}
        </span>
        );
      },
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: user => (
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${
            user.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
          }`}
        >
          {user.status}
        </span>
      ),
    },
    {
      key: 'assignedLocations',
      label: 'Location',
      headerClassName: 'hidden lg:table-cell',
      cellClassName: 'hidden lg:table-cell',
      render: user =>
        user.assignedLocations && user.assignedLocations.length > 0 ? (
          <div className='flex items-center gap-1 text-sm text-gray-600'>
            <MapPin className='h-3 w-3' />
            <span className='truncate max-w-xs'>
              {user.assignedLocations.map(loc => loc.geoLocation.name).join(', ')}
            </span>
          </div>
        ) : (
          <span className='text-gray-400 text-sm'>-</span>
        ),
    },
    {
      key: 'createdAt',
      label: 'Registered',
      sortable: true,
      headerClassName: 'hidden sm:table-cell',
      cellClassName: 'hidden sm:table-cell',
      render: user => <span className='text-sm text-gray-600'>{new Date(user.createdAt).toLocaleDateString()}</span>,
    },
    {
      key: 'actions',
      label: 'Actions',
      render: user => (
        <div className='flex justify-start'>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant='outline' size='sm' className='h-8 px-2'>
                <MoreVertical className='h-4 w-4' />
                <span className='ml-1 hidden sm:inline'>More</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end' className='w-48'>
              {canUpdateStatus && (
                <DropdownMenuItem
                  className='gap-2'
                  onClick={() => setUpdateDialog({ isOpen: true, user })}
                >
                  <Edit className='h-4 w-4' />
                  <span>{dictionary['users.update'] || 'Edit User'}</span>
                </DropdownMenuItem>
              )}
              {canAssignLocations && (
                <DropdownMenuItem
                  className='gap-2'
                  onClick={() => setTransferLocationDialog({ isOpen: true, user })}
                >
                  <ArrowRightLeft className='h-4 w-4' />
                  <span>
                    {(user.assignedLocations?.length ?? 0) === 0
                      ? 'Assign Location'
                      : (user.assignedLocations?.length ?? 0) === 1
                        ? 'Transfer Location'
                        : 'Manage Locations'}
                  </span>
                </DropdownMenuItem>
              )}
              {user.status === 'INACTIVE' && (
                <>
                  {canApproveUser && (
                    <DropdownMenuItem
                      className='gap-2'
                      onClick={() =>
                        setConfirmDialog({
                          isOpen: true,
                          userId: user.id,
                          userName: user.name,
                          action: 'approve',
                        })
                      }
                    >
                      <UserCheck className='h-4 w-4' />
                      <span>Approve</span>
                    </DropdownMenuItem>
                  )}
                  {canApproveUser && (
                    <DropdownMenuItem
                      variant='destructive'
                      className='gap-2'
                      onClick={() =>
                        setRejectDialog({
                          isOpen: true,
                          userId: user.id,
                          userName: user.name,
                        })
                      }
                    >
                      <UserX className='h-4 w-4' />
                      <span>Reject</span>
                    </DropdownMenuItem>
                  )}
                </>
              )}
              {user.status === 'ACTIVE' && currentUser?.id !== user.id && canUpdateStatus && (
                <DropdownMenuItem
                  variant='destructive'
                  className='gap-2'
                  onClick={() =>
                    setConfirmDialog({
                      isOpen: true,
                      userId: user.id,
                      userName: user.name,
                      action: 'deactivate',
                    })
                  }
                >
                  <XCircle className='h-4 w-4' />
                  <span>Deactivate</span>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className='p-4 sm:p-6 space-y-4 sm:space-y-6 max-w-[1600px] mx-auto'>
        <div className='flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center'>
          <div>
            <h1 className='text-xl sm:text-2xl font-bold text-gray-900'>{dictionary['users.title']}</h1>
            <p className='text-gray-600 mt-1'>{dictionary['users.subtitle']}</p>
          </div>
          <div className='flex gap-2 w-full sm:w-auto'>
            {canCreateUser && (
              <Button type='button' variant='outline' onClick={() => setBulkImportDialogOpen(true)} className='shrink-0'>
                <Upload className='h-4 w-4 mr-2' />
                Bulk import
              </Button>
            )}
            {canAssignLocations && (
              <Button type='button' variant='outline' onClick={() => setBulkTransferDialogOpen(true)} className='shrink-0'>
                <ArrowRightLeft className='h-4 w-4 mr-2' />
                Bulk transfer
              </Button>
            )}
            {canCreateUser && (
              <Button type='button' onClick={() => setCreateDialogOpen(true)} className='shrink-0'>
                <UserPlus className='h-4 w-4 mr-2' />
                {dictionary['users.create_user'] ?? 'Create user'}
              </Button>
            )}
          </div>
        </div>

        <div>
          <div className='mb-4'>
            <p className='text-sm text-gray-600'>
              {statusFilter === 'INACTIVE'
                ? dictionary['users.pending_desc']
                : statusFilter === 'ACTIVE'
                  ? dictionary['users.active_desc']
                  : dictionary['users.all_desc']}
            </p>
          </div>

          {/* Search Input */}
          <div className='mb-4'>
            <div className='relative'>
              <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4' />
              <Input
                type='text'
                placeholder={dictionary['users.search_placeholder'] || 'Search users by name or email...'}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className='pl-10'
              />
            </div>
          </div>

          {/* Status Filters */}
          <div className='mb-4'>
            <Tabs value={statusFilter ?? 'ALL'} onValueChange={v => setStatusFilter(v === 'ALL' ? undefined : (v as 'ACTIVE' | 'INACTIVE'))}>
              <TabsList className='inline-flex w-auto'>
                <TabsTrigger value='ALL'>{dictionary['users.all']}</TabsTrigger>
                <TabsTrigger value='INACTIVE'>{dictionary['users.pending']}</TabsTrigger>
                <TabsTrigger value='ACTIVE'>{dictionary['users.active']}</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* Role + location filters */}
          <div className='mb-4 flex flex-col md:flex-row gap-3 items-start'>
            <div className='w-full md:w-64 flex-shrink-0'>
              <label className='block text-xs font-medium text-gray-600 mb-1'>
                {dictionary['users.filter_role'] || 'Role'}
              </label>
              <Input
                type='select'
                value={roleFilter}
                onChange={e => setRoleFilter(String(e.target.value ?? ''))}
                placeholder={dictionary['users.filter_all_roles'] || 'All roles'}
                options={[
                  { value: '', label: dictionary['users.filter_all_roles'] || 'All roles' },
                  ...roleOptions.map(role => ({ value: role.id, label: role.name })),
                ]}
              />
            </div>
            <div className='flex-1 min-w-0'>
              <div className='flex items-center justify-between mb-1'>
                <label className='block text-xs font-medium text-gray-600'>
                  {dictionary['users.filter_location'] || 'Location'}
                </label>
                {(roleFilter || locationFilter) && (
                  <button
                    type='button'
                    onClick={() => {
                      setRoleFilter('');
                      setLocationFilter(undefined);
                    }}
                    className='inline-flex items-center gap-1 text-xs text-primary hover:underline'
                  >
                    <XCircle className='h-3 w-3' />
                    {dictionary['users.clear_filters'] || 'Clear filters'}
                  </button>
                )}
              </div>
              <SingleLocationSelector
                selectedLocationId={locationFilter}
                onLocationChange={locationId => setLocationFilter(locationId)}
                compact
              />
            </div>
          </div>

          {/* Bulk action bar */}
          {selectedUserIds.size > 0 && canUpdateStatus && (
            <div className='mb-4 flex items-center gap-3 px-4 py-2.5 rounded-lg bg-secondary border border-primary/20'>
              <span className='text-sm font-medium text-primary'>
                {selectedUserIds.size} user{selectedUserIds.size > 1 ? 's' : ''} selected
              </span>
              <div className='flex gap-2 ml-auto'>
                <Button
                  type='button'
                  size='sm'
                  variant='outline'
                  onClick={() => handleBulkCentralLevel(true)}
                  disabled={bulkCentralLevelMutation.isPending}
                >
                  <Shield className='h-4 w-4 mr-1.5' />
                  Grant central-level
                </Button>
                <Button
                  type='button'
                  size='sm'
                  variant='outline'
                  onClick={() => handleBulkCentralLevel(false)}
                  disabled={bulkCentralLevelMutation.isPending}
                >
                  <ShieldOff className='h-4 w-4 mr-1.5' />
                  Revoke central-level
                </Button>
                <Button
                  type='button'
                  size='sm'
                  variant='outline'
                  onClick={() => setSelectedUserIds(new Set())}
                >
                  Clear selection
                </Button>
              </div>
            </div>
          )}

          {/* Users Table */}
          <Table
            columns={columns}
            data={users || []}
            isLoading={isLoading}
            enableSearch={false}
            enablePagination={false}
            emptyMessage={dictionary['users.no_users']}
          />

          {/* Server-side Pagination */}
          {totalItems > 0 && (
            <div className='mt-4'>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={limit => {
                  setItemsPerPage(limit);
                  setCurrentPage(1);
                }}
                showItemsPerPage={true}
                itemsPerPageOptions={[10, 20, 50, 100]}
              />
            </div>
          )}
        </div>
      </div>

      {/* Approve Confirmation Dialog */}
      <ConfirmDialog
        open={confirmDialog.isOpen && confirmDialog.action === 'approve'}
        onOpenChange={open => !open && setConfirmDialog({ isOpen: false, userId: '', userName: '', action: null })}
        onConfirm={handleApproveUser}
        title='Approve User'
        description={`Are you sure you want to approve ${confirmDialog.userName}? They will gain access to the system.`}
        confirmText='Approve'
        variant='success'
        isLoading={approveUserMutation.isPending}
      />

      {/* Deactivate Confirmation Dialog */}
      <ConfirmDialog
        open={confirmDialog.isOpen && confirmDialog.action === 'deactivate'}
        onOpenChange={open => !open && setConfirmDialog({ isOpen: false, userId: '', userName: '', action: null })}
        onConfirm={handleDeactivateUser}
        title='Deactivate User'
        description={`Are you sure you want to deactivate ${confirmDialog.userName}? They will lose access to the system.`}
        confirmText='Deactivate'
        variant='danger'
        isLoading={deactivateUserMutation.isPending}
      />

      {/* Reject User Dialog */}
      <RejectUserDialog
        open={rejectDialog.isOpen}
        onOpenChange={open => !open && setRejectDialog({ isOpen: false, userId: '', userName: '' })}
        onConfirm={handleRejectUser}
        userName={rejectDialog.userName}
        isLoading={rejectUserMutation.isPending}
      />

      {/* Edit User Dialog */}
      {updateDialog.user && (
        <UpdateUserDialog
          open={updateDialog.isOpen}
          onOpenChange={open => !open && setUpdateDialog({ isOpen: false, user: null })}
          onConfirm={handleUpdateUser}
          user={updateDialog.user}
          canChangeRole={hasPermission(currentUser as any, 'USERS_UPDATE_STATUS')}
          canAssignLocations={hasPermission(currentUser as any, 'USERS_ASSIGN_LOCATIONS')}
          isLoading={updateUserMutation.isPending}
        />
      )}

      {/* Transfer Location Dialog */}
      {transferLocationDialog.user && (
        <TransferUserLocationDialog
          open={transferLocationDialog.isOpen}
          onOpenChange={open => !open && setTransferLocationDialog({ isOpen: false, user: null })}
          user={transferLocationDialog.user}
        />
      )}

      {canCreateUser && (
        <CreateUserDialog
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
          onConfirm={handleCreateUser}
          isLoading={createUserMutation.isPending}
        />
      )}

      {canCreateUser && (
        <BulkImportDialog
          open={bulkImportDialogOpen}
          onOpenChange={setBulkImportDialogOpen}
          onDownloadTemplate={downloadTemplateMutation.mutate}
          onImport={handleBulkImport}
          isDownloading={downloadTemplateMutation.isPending}
          isImporting={bulkImportMutation.isPending}
        />
      )}

      {canAssignLocations && (
        <BulkTransferLocationsDialog
          open={bulkTransferDialogOpen}
          onOpenChange={setBulkTransferDialogOpen}
          onDownloadTemplate={downloadBulkTransferTemplateMutation.mutate}
          onPreview={handleBulkTransferPreview}
          onCommit={handleBulkTransferCommit}
          isDownloading={downloadBulkTransferTemplateMutation.isPending}
          isPreviewing={bulkTransferPreviewMutation.isPending}
          isCommitting={bulkTransferCommitMutation.isPending}
        />
      )}
    </>
  );
}
